import React from 'react';
import { Link } from 'react-router-dom';
import { ListingCareer } from '../types';
import { PLACEHOLDER_IMAGES, toPublicUrl, getRemoteFallback } from '../constants';
import { ArrowRight } from 'lucide-react';
import SmartImage from './SmartImage';
import { track } from '../services/track';

interface CareerCardProps {
  career: ListingCareer;
  priority?: 'high' | 'low' | 'auto';
}

const CareerCard: React.FC<CareerCardProps> = ({ career, priority = 'auto' }) => {
  const v = (import.meta as any).env?.VITE_BUILD_ID || '';
  const withV = (u: string) => (u && v && !u.startsWith('http') ? `${u}${u.includes('?') ? '&' : '?'}v=${v}` : u);

  const cardSrc = toPublicUrl(career?.images?.card) || toPublicUrl(`/images/careers/${career.slug}/card.webp`);

  return (
    <Link 
      to={`/careers/${career.slug}`} 
      onClick={() => track('career_open', { slug: career.slug, context: 'card' })}
      className="group flex flex-col h-full bg-brand-light border border-brand-border rounded-xl overflow-hidden hover:border-brand-cyan transition-all duration-300 hover:shadow-lg hover:shadow-brand-cyan/10"
    >
      <div className="relative w-full aspect-video overflow-hidden bg-brand-dark">
        <SmartImage 
          src={withV(cardSrc)} 
          alt={career.title}
          fallbackSrc={getRemoteFallback(career.title, 800, 450)}
          width={800}
          height={450}
          priority={priority}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      
      <div className="flex flex-col flex-grow p-5">
        <div className="mb-3">
          <span className="inline-block px-2 py-1 rounded bg-brand-cyan/10 border border-brand-cyan/20 text-xs font-semibold text-brand-cyan tracking-wide uppercase">
            {career.category}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-lg text-brand-white group-hover:text-brand-cyan transition-colors mb-2 line-clamp-1">
          {career.title} {career.emoji && <span className="ml-1" role="img" aria-label="emoji">{career.emoji}</span>}
        </h3>
        <p className="text-brand-grey2 text-sm leading-relaxed mb-4 line-clamp-1">
          {career.tagline || "Discover your future in " + career.category}
        </p>
        <div className="mt-auto pt-3 border-t border-brand-border/50 flex items-center text-brand-cyan text-sm font-medium">
          Explore Path <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
};

export default CareerCard;
